import type { ComparePayload, CustomerSide } from '../api';
import type { IdentityTranslate } from '../i18n';
import { Money, Moment } from '../../../shell/kit';

function Side({ side, t }: { side: CustomerSide; t: IdentityTranslate }) {
  return (
    <dl className="space-y-1.5 text-sm">
      <Row label={t('identity.compare.status')} value={side.status} />
      <Row label={t('identity.compare.phone')} value={side.phone ?? '—'} />
      <Row
        label={t('identity.compare.phones')}
        value={side.phones.length > 0 ? side.phones.join(', ') : '—'}
      />
      <Row
        label={t('identity.compare.vehicles')}
        value={
          side.vehicles.length === 0
            ? '—'
            : side.vehicles
                .map((v) => `${v.plate} (${v.role}${v.active ? '' : ', закрыта'})`)
                .join(', ')
        }
      />
      <Row label={t('identity.compare.visits')} value={String(side.visitCount)} />
      <Row label={t('identity.compare.lastVisit')} value={<Moment iso={side.lastVisitAt} />} />
      <Row label={t('identity.compare.paid')} value={<Money minor={side.paidMinor} />} />
      <Row label={t('identity.compare.balance')} value={<Money minor={side.balanceMinor} />} />
      <Row label={t('identity.compare.feedback')} value={String(side.feedbackCount)} />
      <Row label={t('identity.compare.created')} value={<Moment iso={side.createdAt} />} />
      <Row
        label={t('identity.compare.consents')}
        value={
          side.consents.length === 0
            ? '—'
            : side.consents
                .slice(0, 4)
                .map((c) => `${c.kind}: ${c.active ? '✓' : '✕'}`)
                .join(', ')
        }
      />
      <Row
        label={t('identity.compare.merges')}
        value={side.merges.length === 0 ? '—' : String(side.merges.length)}
      />
    </dl>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 text-ink-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

export function ComparePanel({
  payload,
  t,
  onKeep,
}: {
  payload: ComparePayload;
  t: IdentityTranslate;
  onKeep?: (primaryId: string, sourceId: string) => void;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {[payload.left, payload.right].map((side, index) => {
        const other = index === 0 ? payload.right : payload.left;
        return (
          <div key={side.id} className="rounded border border-line p-3">
            <header className="mb-2 flex items-center justify-between gap-2">
              <div>
                <h4 className="text-sm font-semibold text-ink">
                  {index === 0 ? t('identity.compare.left') : t('identity.compare.right')}
                </h4>
                <p className="text-xs text-ink-muted">
                  {side.displayName ?? '—'} · <code>{side.id}</code>
                </p>
              </div>
              {onKeep ? (
                <button
                  type="button"
                  onClick={() => onKeep(side.id, other.id)}
                  className="inline-flex h-7 items-center justify-center rounded-control bg-brand-600 px-2.5 text-xs font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
                >
                  {index === 0 ? t('identity.compare.keepLeft') : t('identity.compare.keepRight')}
                </button>
              ) : null}
            </header>
            <Side side={side} t={t} />
          </div>
        );
      })}
    </div>
  );
}
